import { create } from 'zustand';
import type { FocusSession } from '@mindrift/shared';

interface TimerState {
  session: FocusSession | null;
  startedAt: number | null;
  durationMs: number;
  remainingMs: number;
  status: 'idle' | 'running' | 'paused' | 'ended' | 'cancelled';
  pendingTaskId: string | null;
  setSession: (session: FocusSession | null, durationMs: number) => void;
  rehydrate: (session: FocusSession, startedAt: number, durationMs: number) => void;
  tick: (now: number) => void;
  setStatus: (status: TimerState['status']) => void;
  clear: () => void;
  setPendingTask: (id: string | null) => void;
}

export const useFocusTimer = create<TimerState>((set, get) => ({
  session: null,
  startedAt: null,
  durationMs: 0,
  remainingMs: 0,
  status: 'idle',
  pendingTaskId: null,
  setSession(session, durationMs) {
    const startedAtMs = session ? new Date(session.startedAt).getTime() : null;
    const remainingMs = session && startedAtMs ? Math.max(0, durationMs - (Date.now() - startedAtMs)) : 0;
    set({
      session,
      durationMs,
      startedAt: startedAtMs,
      remainingMs,
      status: session ? (session.status === 'running' ? 'running' : 'ended') : 'idle',
    });
  },
  rehydrate(session, startedAt, durationMs) {
    const remainingMs = Math.max(0, durationMs - (Date.now() - startedAt));
    set({
      session,
      startedAt,
      durationMs,
      remainingMs,
      status: remainingMs <= 0 ? 'ended' : 'running',
    });
  },
  tick(now) {
    const { startedAt, durationMs, status } = get();
    if (status !== 'running' || !startedAt) return;
    const elapsed = now - startedAt;
    const remaining = Math.max(0, durationMs - elapsed);
    set({ remainingMs: remaining });
    if (remaining <= 0) set({ status: 'ended' });
  },
  setStatus(status) {
    set({ status });
  },
  clear() {
    set({ session: null, startedAt: null, durationMs: 0, remainingMs: 0, status: 'idle' });
  },
  setPendingTask(id) {
    set({ pendingTaskId: id });
  },
}));
